// Stage 7 — Command registry.
//
// One source of truth for every user-invocable action: keyboard hotkeys,
// command-palette entries, and the help-panel "Shortcuts" tab all read from
// here. Pre-Stage-7 the keymap was a 70-line switch in `shortcuts.ts`; that
// path now installs a single global key listener that resolves chords through
// this registry. Adding a command in one place lights it up everywhere.
//
// Persistence: a single localStorage key (`magviz:shortcuts:v1`) layers user
// rebindings on top of defaults at startup. Stage 7 ships data-model + storage
// only — the editor UI lands later (webpic M7/v0.2).

export interface CommandDef {
    /** Stable id, e.g. `tab.open.reductions`. Used as the persistence key. */
    readonly id: string;
    /** User-visible label rendered in palette + help tab. */
    readonly label: string;
    /** Coarse bucket for help-tab grouping. */
    readonly category: 'Palette' | 'Navigation' | 'Primitives' | 'Selections' | 'Reductions' | 'Theme' | 'View' | 'Misc';
    /** Free-text tokens the fuzzy matcher consumes alongside `label`. */
    readonly aliases?: ReadonlyArray<string>;
    /** Default key chords. Empty means palette-only (no global hotkey). */
    readonly keys?: ReadonlyArray<string>;
    /** Allow the chord to fire while focus is in an input/textarea. */
    readonly allowWhileTyping?: boolean;
    /** When false, the command is greyed out in the palette and the chord no-ops. */
    readonly enabled?: boolean;
    /** Side-effect. Called with no args; pull state from singletons. */
    readonly run: () => void;
}

const LS_KEY = 'magviz:shortcuts:v1';

const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');

/** "⌘K" / "Ctrl+K" / "Shift+G" — a display string per chord. */
export function formatChord(chord: string): string {
    if (!chord) return '';
    return chord
        .split('+')
        .map((part) => {
            const p = part.trim();
            if (/^cmd$|^meta$/i.test(p)) return isMac ? '⌘' : 'Win';
            if (/^ctrl$/i.test(p)) return isMac ? '⌃' : 'Ctrl';
            if (/^alt$|^opt$/i.test(p)) return isMac ? '⌥' : 'Alt';
            if (/^shift$/i.test(p)) return isMac ? '⇧' : 'Shift';
            return p;
        })
        .join(isMac ? '' : '+');
}

/** Normalize a chord string for storage / comparison: lower-case mods,
 *  CapitalCase main key, ordered Ctrl > Alt > Shift > Meta. */
export function normalizeChord(chord: string): string {
    const parts = chord
        .split('+')
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length === 0) return '';
    const mods = new Set<string>();
    let main = '';
    for (const raw of parts) {
        const p = raw.toLowerCase();
        if (p === 'cmd' || p === 'meta') mods.add('Meta');
        else if (p === 'ctrl' || p === 'control') mods.add('Ctrl');
        else if (p === 'alt' || p === 'opt' || p === 'option') mods.add('Alt');
        else if (p === 'shift') mods.add('Shift');
        else main = raw.length === 1 ? raw.toUpperCase() : raw[0]!.toUpperCase() + raw.slice(1);
    }
    if (!main) return '';
    const order = ['Ctrl', 'Alt', 'Shift', 'Meta'];
    const out: string[] = [];
    for (const m of order) if (mods.has(m)) out.push(m);
    out.push(main);
    return out.join('+');
}

/** Build the chord identifier for a live KeyboardEvent. Returns '' for plain
 *  modifier-only presses (Shift on its own etc.). Exported for tests so the
 *  Node-mode Vitest suite doesn't need jsdom to exercise chord matching. */
export function chordFromEvent(e: KeyboardEvent): string {
    const mods: string[] = [];
    if (e.ctrlKey) mods.push('Ctrl');
    if (e.altKey) mods.push('Alt');
    if (e.shiftKey) mods.push('Shift');
    if (e.metaKey) mods.push('Meta');
    const k = e.key;
    if (!k || k === 'Control' || k === 'Alt' || k === 'Shift' || k === 'Meta') return '';
    // Normalise the printable key onto the same shape as `normalizeChord`.
    let main: string;
    if (k === ' ') main = 'Space';
    else if (k === 'Escape') main = 'Escape';
    else if (k === 'Enter') main = 'Enter';
    else if (k === 'Tab') main = 'Tab';
    else if (k === 'Backspace') main = 'Backspace';
    else if (k.startsWith('Arrow')) main = k;
    else if (k.length === 1) main = k.toUpperCase();
    else main = k[0]!.toUpperCase() + k.slice(1);
    return [...mods, main].join('+');
}

function loadOverrides(): Record<string, string[]> {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') return {};
        const out: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
            if (typeof k !== 'string' || !Array.isArray(v)) continue;
            const keys = v.filter((x): x is string => typeof x === 'string').map(normalizeChord).filter(Boolean);
            out[k] = keys;
        }
        return out;
    } catch (e) {
        console.warn('[shortcutRegistry] override parse failed, falling back to defaults', e);
        return {};
    }
}

function saveOverrides(map: Record<string, string[]>): void {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(map));
    } catch {
        // quota / private mode — silently drop, defaults still work
    }
}

export interface ShortcutRegistry {
    register(def: CommandDef): () => void;
    unregister(id: string): void;
    get(id: string): CommandDef | undefined;
    list(): readonly CommandDef[];
    /** Live key chords for `id` after applying user overrides. */
    keysFor(id: string): readonly string[];
    /** Replace the binding for `id`. Empty array unbinds. Persists. */
    rebind(id: string, keys: ReadonlyArray<string>): void;
    /** Run a command by id. Returns false when missing or disabled. */
    invoke(id: string): boolean;
    /** Notify when commands or bindings change. */
    subscribe(fn: () => void): () => void;
    /** Wire the global keydown listener. Returns a disposer. */
    installGlobalKeyListener(target?: Window | Document): () => void;
    /** Test-only — exercise the same chord-matching path the global listener
     *  uses, without requiring a DOM. Returns true when a command fired. */
    _handleChordForTests(chord: string, isTyping: boolean): boolean;
}

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return TYPING_TAGS.has(target.tagName);
}

export function createShortcutRegistry(): ShortcutRegistry {
    const commands = new Map<string, CommandDef>();
    const overrides: Record<string, string[]> = loadOverrides();
    const subscribers = new Set<() => void>();

    const notify = (): void => {
        for (const fn of subscribers) {
            try { fn(); } catch (e) { console.error('[shortcutRegistry] subscriber error', e); }
        }
    };

    const keysFor = (id: string): readonly string[] => {
        const override = overrides[id];
        if (override !== undefined) return override;
        const def = commands.get(id);
        if (!def?.keys) return [];
        return def.keys.map(normalizeChord).filter(Boolean);
    };

    const findByChord = (chord: string, typing: boolean): CommandDef | undefined => {
        const normalized = normalizeChord(chord);
        if (!normalized) return undefined;
        for (const def of commands.values()) {
            if (def.enabled === false) continue;
            if (typing && !def.allowWhileTyping) continue;
            const keys = keysFor(def.id);
            for (const k of keys) {
                if (k === normalized) return def;
                // ⌘K and Ctrl+K are interchangeable for the open-palette case
                // and a few other cross-platform shortcuts. Treat Meta as Ctrl
                // when matching anywhere that the registered chord uses Ctrl,
                // and vice versa, so a single registered chord covers both.
                if (k.includes('Meta') && normalized === k.replace('Meta', 'Ctrl')) return def;
                if (k.includes('Ctrl') && normalized === k.replace('Ctrl', 'Meta')) return def;
            }
        }
        return undefined;
    };

    const registry: ShortcutRegistry = {
        register(def) {
            if (commands.has(def.id)) {
                console.warn(`[shortcutRegistry] re-registering "${def.id}"`);
            }
            commands.set(def.id, def);
            notify();
            return () => registry.unregister(def.id);
        },
        unregister(id) {
            if (commands.delete(id)) notify();
        },
        get(id) {
            return commands.get(id);
        },
        list() {
            return Array.from(commands.values());
        },
        keysFor,
        rebind(id, keys) {
            const normalized = keys.map(normalizeChord).filter(Boolean);
            overrides[id] = normalized;
            saveOverrides(overrides);
            notify();
        },
        invoke(id) {
            const def = commands.get(id);
            if (!def || def.enabled === false) return false;
            try { def.run(); } catch (e) { console.error(`[shortcutRegistry] "${id}" run failed`, e); return false; }
            return true;
        },
        subscribe(fn) {
            subscribers.add(fn);
            return () => { subscribers.delete(fn); };
        },
        _handleChordForTests(chord, isTyping) {
            const def = findByChord(chord, isTyping);
            if (!def) return false;
            try { def.run(); } catch (e) { console.error(`[shortcutRegistry] "${def.id}" run failed`, e); return false; }
            return true;
        },
        installGlobalKeyListener(target = window) {
            const onKeyDown = (e: Event): void => {
                const ke = e as KeyboardEvent;
                const chord = chordFromEvent(ke);
                if (!chord) return;
                const typing = isTypingTarget(ke.target);
                const def = findByChord(chord, typing);
                if (!def) return;
                ke.preventDefault();
                ke.stopPropagation();
                try { def.run(); } catch (err) { console.error(`[shortcutRegistry] "${def.id}" run failed`, err); }
            };
            target.addEventListener('keydown', onKeyDown, true);
            return () => {
                try { target.removeEventListener('keydown', onKeyDown, true); } catch { /* swallow */ }
            };
        },
    };

    return registry;
}

// Module-level singleton — there's only one keyboard, only one registry.
let singleton: ShortcutRegistry | null = null;

export function getShortcutRegistry(): ShortcutRegistry {
    if (!singleton) singleton = createShortcutRegistry();
    return singleton;
}

/** Test-only — reset the singleton between Vitest runs. Never used at runtime. */
export function _resetShortcutRegistryForTests(): void {
    singleton = null;
}
